import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UseAuth, UseUserDetails, UseIsDemo, UseIsAdmin } from "./auth";
import { types } from "../redux/kpi/types";
import { storeMyKpis } from '../services/kpiStorage';
import { saveOfflineKpi, deletePendingKpi } from '../services/offlineStorage';
import { useOnlineStatus } from "./useNetwork";
import { generateUuid } from "../utils/generateUuid";

import moment from 'moment';
import { GET_LINKS } from '../api/links';
import startCase from 'lodash/startCase';

import {
  UseSelectedWorkspace,
  UseUserIdList,
  UseUsersDictionary,
  UseYetiStats,
} from "./company";
import yetiProfile from "../constants/yetiProfile";
import { 
  ADD_USER_KPI, 
  GET_GROUP_KPIS, 
  GET_ALL_USER_KPIS, 
  UPDATE_USER_KPI, 
  DELETE_USER_KPI,
  UPDATE_TOTAL_SCORE,
  UPDATE_GROUP_KPI,
  CREATE_CUSTOM_KPI,
  DISABLE_GROUP_KPI,
} from '../api/kpi';

const addKpiObjects = (kpiSum, plusKpi) => {
  Object.keys(plusKpi).forEach((key) => {
    if (kpiSum[key]) {
      kpiSum[key].pts += plusKpi[key]?.pts;
      kpiSum[key].amt += plusKpi[key]?.amt;
    } else {
      kpiSum[key] = { ...plusKpi[key] };
    }
    kpiSum.total += plusKpi[key]?.pts ?? 0;
  });
  return kpiSum;
};

export const useKpiData = () => {
  const dispatch = useDispatch();
  const authenticated = UseAuth();
  const { _id: userId, companyId } = UseUserDetails();
  const isAdmin = UseIsAdmin();
  const workspace = UseSelectedWorkspace();
  const isLoading = useSelector((state) => state.kpiReducer.isLoading);

  const fetchKpiData = useCallback(async () => {
    if (!authenticated || !workspace?._id) return;

    try {
      dispatch({ type: types.REFRESH_KPIS });
      
      // Fetch group KPIs
      const targetKpis = await GET_GROUP_KPIS(companyId);
      
      if (!targetKpis.data?.length) {
        dispatch({ type: types.GET_KPI_SCORE_EMPTY });
        return;
      }

      // Process company KPIs
      const allCompanyKpis = targetKpis.data.map(({
        _id, name, pointScale, target, requiredInputs, model,
        groupId, parameters, sourceId, source, integration,
        integrationId, integrationMeta, isAdminAccess, metricType,
        optional, isDraft, pointValue
      }) => ({
        _id,
        groupId,
        metricType,
        optional,
        isDraft,
        pointValue,
        pointScale: pointScale ?? model.defaultPointScale,
        defaultPointScale: model.defaultPointScale,
        target: target ?? model.defaultTarget,
        title: startCase(name ?? model.type),
        kind: model.kind,
        type: model.type,
        kpiModelId: model._id,
        direction: model.direction,
        isManual: !sourceId,
        isShared: !!(parameters?.level || source?.parameters?.level),
        isCustom: model.isTemplate === true,
        isAdminAccess: isAdminAccess ?? false,
        integrationId,
        ...(integrationMeta && { integrationMeta }),
        sourceId,
        source: integration?.code,
        sourceName: integration?.name,
        wholeMonth: source?.parameters?.calc && source?.parameters?.calc !== "sum",
        calc: source?.parameters?.calc,
        gap: source?.parameters?.gap,
        minimum: parameters?.minimum ?? 0,
        parameters: parameters ?? null,
        requiredInputs: requiredInputs?.length > 0 ? requiredInputs : model.defaultInputs ?? null,
      })).sort((a, b) => b.pointScale * b.target - a.pointScale * a.target);

      // Separate draft and active KPIs
      const draftKpis = allCompanyKpis.filter(kpi => kpi.isDraft);
      const companyKpis = {};
      allCompanyKpis.filter(kpi => !kpi.isDraft).forEach((groupKpi) => {
        if (companyKpis[groupKpi.groupId]) {
          companyKpis[groupKpi.groupId].push(groupKpi);
        } else {
          companyKpis[groupKpi.groupId] = [groupKpi];
        }
      });

      // Process workspace KPIs
      let groupKpis = [];
      if (isAdmin) {
        const links = await GET_LINKS(companyId);
        const workspaceKpis = companyKpis[workspace._id] ?? [];
        groupKpis = workspaceKpis.map((groupKpi) => {
          const kpiLink = links.data.data.find((link) => link.kpiId === groupKpi._id);
          return kpiLink ? { ...groupKpi, kpiLinks: kpiLink } : groupKpi;
        });
      } else {
        groupKpis = companyKpis[workspace._id] ?? [];
      }

      // Fetch and process user scores
      const scoreKpis = await GET_ALL_USER_KPIS(companyId, workspace._id);

      const userScores = [];
      const sharedScores = [];

      scoreKpis.data.scores.forEach((score) => {
        if (score.scope === "User") {
          userScores.push(score);
        } else {
          sharedScores.push(...score.days);
        }
      });

      if (sharedScores.length) {
        scoreKpis.data.users.forEach((user) => {
          const idx = userScores.findIndex((u) => u.ownerId === user);
          if (idx >= 0) {
            userScores[idx].days.push(...sharedScores);
          } else {
            userScores.push({
              ownerId: user,
              days: [...sharedScores],
            });
          }
        });
      }

      // Calculate time-based summaries
      const day = moment().startOf("day");
      const yesterday = moment().startOf("day").subtract(1, "days");
      const week = moment().startOf("week");
      const month = moment().startOf("month");

      let myKpis = null;
      const usersKpis = Array.isArray(userScores) ? userScores.map((userScore) => {
        const summary = {
          userId: userScore.ownerId,
          yesterday: { total: 0 },
          today: { total: 0 },
          week: { total: 0 },
          month: { total: 0 },
        };

        userScore.days.forEach((dayData) => {
          const dateRef = moment(dayData.day);
          if (dateRef >= day) {
            addKpiObjects(summary.today, dayData.kpis);
          } else if (dateRef >= yesterday) {
            addKpiObjects(summary.yesterday, dayData.kpis);
          }
          if (dateRef >= week) {
            addKpiObjects(summary.week, dayData.kpis);
          }
          if (dateRef >= month) {
            addKpiObjects(summary.month, dayData.kpis);
          }
        });

        if (summary.userId === userId) {
          myKpis = summary;
        }

        return summary;
      }) : [];

      // Calculate admin view averages
      const totalKpis = { total: 0 };
      const teamScores = usersKpis
        .map(scores => {
          const { total, ...rest } = scores.month;
          return total ? rest : null;
        })
        .filter(Boolean);

      teamScores.forEach(scores => {
        addKpiObjects(totalKpis, scores);
      });

      Object.keys(totalKpis).forEach(key => {
        if (key !== "total") {
          const scorersCount = teamScores.reduce(
            (count, score) => count + (score[key] ? 1 : 0),
            0
          );
          totalKpis[key].amt = Math.round((totalKpis[key].amt / scorersCount) * 100) / 100;
          totalKpis[key].pts = Math.round((totalKpis[key].pts / scorersCount) * 100) / 100;
          totalKpis[key].scorersCount = scorersCount;
        } else {
          totalKpis.total = Math.round((totalKpis.total / teamScores.length) * 100) / 100;
        }
      });

      dispatch({
        type: types.GET_KPI_SCORE_SUCCESS,
        companyKpis,
        groupKpis,
        draftKpis,
        usersKpis: usersKpis.sort((a, b) => b.month.total - a.month.total),
        myKpis,
        adminKpis: { month: totalKpis },
      });
    } catch (err) {
      dispatch({ type: types.GET_KPI_SCORE_FAILED, error: err.message });
    }
  }, [authenticated, companyId, dispatch, isAdmin, userId, workspace]);

  useEffect(() => {
    fetchKpiData();
  }, [fetchKpiData]);

  return { isLoading, refetch: fetchKpiData };
}; 

export const UseKpiRefresh = () => {
  const { refetch } = useKpiData();
  return refetch;
};

export const UseKpiLoading = () => {
  const { isLoading } = useKpiData();
  return isLoading;
};

export const UseGroupKpis = () => {
  const groupKpis = useSelector((state) => state.kpiReducer.groupKpis);
  return groupKpis;
};

export const UseCompanyKpis = () => {
  const companyKpis = useSelector((state) => state.kpiReducer.companyKpis);
  return companyKpis;
};

/**
 * Returns user scores, returns workspace average instead if admin
 * @param {bool} forceAdmin if true, returns workspace average scores whether current user is admin or not
 */
export const UseMyKpis = (forceAdmin = false) => {
  const myKpis = useSelector((state) => state.kpiReducer.myKpis);
  const adminKpis = useSelector((state) => state.kpiReducer.adminKpis);
  const topKpis = useSelector((state) => state.kpiReducer.usersKpis?.[0]);
  const [kpis, setKpis] = useState(myKpis);
  const isDemo = UseIsDemo();
  const isAdmin = UseIsAdmin();

  useEffect(() => {
    if (isDemo) {
      setKpis(topKpis);
    } else if (isAdmin || forceAdmin) {
      setKpis(adminKpis);
    } else {
      setKpis(myKpis);
    }
    storeMyKpis(myKpis);
  }, [myKpis, adminKpis, topKpis, isDemo, isAdmin, forceAdmin]);

  return kpis;
};

/**
 *
 * @param {string} period accepts yesterday, today, week, month
 * @param {*} month date reference
 * @returns array of player score objects in descending score order
 */
export const UseTopPlayers = (period = "month", month = null) => {
  const [stats, setStats] = useState(null);
  const monthRef = month?.format("YYYY-MM");
  const usersKpis = useSelector((state) =>
    monthRef
      ? state.kpiReducer.historicKpis?.[monthRef]
      : state.kpiReducer.usersKpis,
  );
  const userId = useSelector((state) => state.authReducer.userDetails?._id);
  const usersDict = UseUsersDictionary();
  const yeti = UseYetiStats(!monthRef);

  useEffect(() => {
    if (usersDict && Array.isArray(usersKpis) && yeti) {
      const topPlayers = usersKpis.map((item) => ({
        userId: item.userId,
        scores: item[period],
        today: item.today?.total,
        yesterday: item.yesterday?.total,
        yesterdayRank: item.yesterdayRank,
        ...usersDict[item.userId],
        ...(item.userId === userId && { ownId: true }),
      }));

      topPlayers.push({
        ...yetiProfile,
        scores: yeti[period],
        today: yeti.today.total,
        yesterday: yeti.today.total,
        isYeti: true,
      });

      if (!monthRef) {
        // Previous month's scores; skip yesterday comparison
        // Order based on yesterday's scores and record ranking
        if (period === "today") {
          // Compare points yesterday versus today
          topPlayers.sort((a, b) => b.yesterday - a.yesterday);
        } else {
          // Compare points yesterday versus today, but relative to the month or week's standing
          topPlayers.sort(
            (a, b) => b.scores?.total - b.today - (a.scores?.total - a.today),
          );
        }
        topPlayers.forEach((user, index) => {
          user.yesterdayRank = index + 1;
        });
      }
      topPlayers.sort((a, b) => b.scores?.total - a.scores?.total);
      setStats(topPlayers);
    }
  }, [period, yeti, usersKpis, usersDict, monthRef, userId]);

  return stats;
};

// table of user KPI scores
export const UseKpiScoresTable = (month = null) => {
  const groupKpis = useSelector((state) => state.kpiReducer.groupKpis);
  const monthRef = month?.format("YYYY-MM");
  const usersKpis = useSelector((state) =>
    monthRef
      ? state.kpiReducer.historicKpis?.[monthRef]
      : state.kpiReducer.usersKpis,
  );
  const usersDict = UseUsersDictionary();
  const usersList = UseUserIdList();
  const currentWorkspace = UseSelectedWorkspace();
  const [kpiTable, setKpiTable] = useState([]);

  useEffect(() => {
    // Add top scorers on top of table
    const newKpiTable = [];
    const allUsers = [...usersList];

    if (usersKpis?.length) {
      for (let u = 0; u < usersKpis.length; u++) {
        const user = usersKpis[u];
        const { groupId, isArchived } = usersDict[user.userId] ?? {};

        // Filter user by selected workspace, and not archived
        if (isArchived || groupId !== currentWorkspace._id) continue;

        const kpiScores = [];
        for (let k = 0; k < groupKpis.length; k++) {
          if (user.month[groupKpis[k]._id]) {
            kpiScores.push(user.month[groupKpis[k]._id].amt);
          } else {
            kpiScores.push(0);
          }
        }
        allUsers.splice(allUsers.indexOf(user.userId), 1);
        newKpiTable.push({
          userId: user.userId,
          info: usersDict[user.userId],
          kpis: kpiScores,
          total: user.month.total,
        });
      }
    }

    // Add zero-scorers next
    allUsers.map((user) => {
      const { groupId, isArchived } = usersDict[user] ?? {};

      // Filter user by selected workspace, and not archived
      if (isArchived || groupId !== currentWorkspace._id) return;

      newKpiTable.push({
        userId: user,
        info: usersDict[user],
        kpis: null,
        total: 0,
      });
    });
    setKpiTable(newKpiTable);
  }, [currentWorkspace, usersKpis, groupKpis, usersList, usersDict]);

  return kpiTable;
};

export const UseDraftKpis = () => {
  const draftKpis = useSelector((state) => state.kpiReducer.draftKpis);
  return draftKpis;
};

/**
 * Hook for submitting new KPI records
 */
export const useKpiSubmit = () => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const { _id: adminId, isAdmin } = UseUserDetails();
  const adminKpis = useSelector((state) => state.kpiReducer.adminKpis);
  const myKpis = useSelector((state) => state.kpiReducer.myKpis);
  const monthRef = useSelector(state => state.kpiReducer.usersKpis);

  // let myKpis;
  // if (isAdmin) {
  //   myKpis = adminKpis;
  // } else {
  //   myKpis = userKpis;
  // }

  const groupKpis = useSelector((state) => state.kpiReducer.groupKpis);
  const isOnline = useOnlineStatus();
  const entryId = generateUuid();

  const submitKpi = async (kpiId, userId, month, data, companyId) => {
    setIsSubmitting(false);
    setError(null);
    const monthFormatted = month ? moment(month).format("YYYY-MM") : null;
    const kpiData = groupKpis?.find(kpi => kpi._id === kpiId);
    const totalUsersInActiveWorkspace = adminKpis.month[kpiId]?.scorersCount || 0;

    // Convert FormData to plain object for offline storage
    const plainData = {};
    let hasImage = false;
    if (data instanceof FormData) {
      Array.from(data.entries()).forEach(([key, value]) => {
        if (key === "image" || key === "imageURL") {
          hasImage = true;
        }
        plainData[key] = value;
      });
    } else {
      Object.assign(plainData, data);
    }

    // If offline, handle offline storage
    if (!isOnline || hasImage) {
      try {

        if (hasImage && isOnline) {
          ADD_USER_KPI({
            kpiId,
            companyId,
            userId,
            data,
            month: monthFormatted,
            entryId,
          });
        }else{
          // Dispatch the action to handle offline storage
          // Save to offline storage first
          saveOfflineKpi({
            kpiId,
            userId,
            month: monthFormatted,
            data: plainData,
            companyId,
            entryId
          });
        }

        // Update myKpis in the local state to reflect the new score
        if (myKpis) {
          const today = moment().startOf("day");
          const isToday = !month || moment(month).isSame(today, 'day');
          
          // Create a copy of the current KPI data
          const updatedMyKpis = JSON.parse(JSON.stringify(myKpis));
          let pts = 0;
          let amt = 0;
          // if (isAdmin && adminId === userId) {
          //   // Calculate points based on the KPI model
          //   pts = (kpiData?.pointScale * plainData?.amount) / totalUsersInActiveWorkspace || 0;
          //   amt = (parseFloat(plainData?.amount)) / totalUsersInActiveWorkspace || 0;
          // }else{
            // Calculate points based on the KPI model
            pts = kpiData?.pointScale * plainData?.amount || 0;
            amt = parseFloat(plainData?.amount) || 0;
          // }  

          // Update the monthly scores
          const updatedMonthRef = JSON.parse(JSON.stringify(monthRef));
          const userData = updatedMonthRef.find(user => user.userId === userId);
          if (userData) {
            if (!userData.month[kpiId]) {
              userData.month[kpiId] = { pts: parseFloat(pts), amt: parseFloat(amt) };
              userData.month.total = parseFloat(userData.month.total || 0) + pts;
            }else{
              userData.month[kpiId].pts = parseFloat(userData.month[kpiId].pts || 0) + pts;
              userData.month[kpiId].amt = parseFloat(userData.month[kpiId].amt || 0) + amt;
              userData.month.total = parseFloat(userData.month.total || 0) + pts;
            }
          }

          dispatch({
            type: types.UPDATE_MONTH_KPIS,
            usersKpis: updatedMonthRef
          });

          // if (isAdmin) {
          //   const updatedAdminKpis = { ...myKpis };
            
          //   if (!updatedAdminKpis.month[kpiId]) {
          //     updatedAdminKpis.month[kpiId] = { pts: 0, amt: 0 };
          //   }
          //   if (typeof updatedAdminKpis.month[kpiId].pts !== "number") {
          //     updatedAdminKpis.month[kpiId].pts = pts;
          //   } else {
          //     updatedAdminKpis.month[kpiId].pts += pts;
          //   }
          //   if (typeof updatedAdminKpis.month[kpiId].amt !== "number") {
          //     updatedAdminKpis.month[kpiId].amt = amt;
          //   } else {
          //     updatedAdminKpis.month[kpiId].amt += amt;
          //   }
          //   if (typeof updatedAdminKpis.month.total !== "number") {
          //     updatedAdminKpis.month.total = pts;
          //   } else {
          //     updatedAdminKpis.month.total += pts;
          //   }

          //   dispatch({
          //     type: types.UPDATE_ADMIN_KPIS,
          //     adminKpis: updatedAdminKpis
          //   });
          // } else {// Update the appropriate time period
            if (isToday) {
              // Update today's scores
              if (!updatedMyKpis.today[kpiId]) {
                updatedMyKpis.today[kpiId] = { pts: 0, amt: 0 };
              }
              // Set the values directly instead of adding to prevent concatenation
              updatedMyKpis.today[kpiId].pts = parseFloat(updatedMyKpis.today[kpiId].pts) + pts;
              updatedMyKpis.today[kpiId].amt = parseFloat(updatedMyKpis.today[kpiId].amt) + amt;
              updatedMyKpis.today.total = parseFloat(updatedMyKpis.today.total) + pts;
              
              // Also update week and month
              if (!updatedMyKpis.week[kpiId]) {
                updatedMyKpis.week[kpiId] = { pts: 0, amt: 0 };
              }
              updatedMyKpis.week[kpiId].pts = parseFloat(updatedMyKpis.week[kpiId].pts) + pts;
              updatedMyKpis.week[kpiId].amt = parseFloat(updatedMyKpis.week[kpiId].amt) + amt;
              updatedMyKpis.week.total = parseFloat(updatedMyKpis.week.total) + pts;
              
              if (!updatedMyKpis.month[kpiId]) {
                updatedMyKpis.month[kpiId] = { pts: 0, amt: 0 };
              }
              updatedMyKpis.month[kpiId].pts = parseFloat(updatedMyKpis.month[kpiId].pts) + pts;
              updatedMyKpis.month[kpiId].amt = parseFloat(updatedMyKpis.month[kpiId].amt) + amt;
              updatedMyKpis.month.total = parseFloat(updatedMyKpis.month.total) + pts;
            } else {
              // For historical entries, update only the appropriate period
              const dateRef = moment(month);
              const week = moment().startOf("week");
              const monthStart = moment().startOf("month");
              
              if (dateRef >= week) {
                if (!updatedMyKpis.week[kpiId]) {
                  updatedMyKpis.week[kpiId] = { pts: 0, amt: 0 };
                }
                updatedMyKpis.week[kpiId].pts = parseFloat(updatedMyKpis.week[kpiId].pts) + pts;
                updatedMyKpis.week[kpiId].amt = parseFloat(updatedMyKpis.week[kpiId].amt) + amt;
                updatedMyKpis.week.total = parseFloat(updatedMyKpis.week.total) + pts;
              }
              
              if (dateRef >= monthStart) {
                if (!updatedMyKpis.month[kpiId]) {
                  updatedMyKpis.month[kpiId] = { pts: 0, amt: 0 };
                }
                updatedMyKpis.month[kpiId].pts = parseFloat(updatedMyKpis.month[kpiId].pts) + pts;
                updatedMyKpis.month[kpiId].amt = parseFloat(updatedMyKpis.month[kpiId].amt) + amt;
                updatedMyKpis.month.total = parseFloat(updatedMyKpis.month.total) + pts;
              }
            }
            // Update the Redux store with the new KPI data
            dispatch({
              type: types.UPDATE_MY_KPIS,
              myKpis: updatedMyKpis
            });
          // }
        }
        
        console.log('✅ Offline KPI submission queued');
        return;
      } catch (offlineErr) {
        setError('Failed to store offline data: ' + offlineErr.message);
        throw offlineErr;
      }
    }
    // Online flow
    try {
      const result = await ADD_USER_KPI({
        kpiId,
        companyId,
        userId,
        data,
        month: monthFormatted,
        entryId,
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      // Update myKpis in the local state to reflect the new score
      if (result.data && myKpis) {
        const today = moment().startOf("day");
        const isToday = !month || moment(month).isSame(today, 'day');
        
        // Create a copy of the current KPI data
        const updatedMyKpis = { ...myKpis };

        let pts = 0;
        let amt = 0;
        // if (isAdmin && adminId === userId) {
        //   // Calculate points based on the KPI model
        //   pts = result.data.points / totalUsersInActiveWorkspace || 0;
        //   amt = result.data.amount / totalUsersInActiveWorkspace || 0;
        // }else{
        //   // Calculate points based on the KPI model
        //   pts = result.data.points || 0;
        //   amt = result.data.amount || 0;
        // }

        pts = result.data.points || 0;
        amt = result.data.amount || 0;
        
        // Update the monthly scores
        const updatedMonthRef = JSON.parse(JSON.stringify(monthRef));
        const userData = updatedMonthRef.find(user => user.userId === userId);
        if (userData) {
          if (!userData.month[kpiId]) {
            userData.month[kpiId] = { pts: result.data.points, amt: result.data.amount };
            userData.month.total = parseFloat((userData.month.total || 0) + result.data.points);
            dispatch({
              type: types.UPDATE_MONTH_KPIS,
              usersKpis: updatedMonthRef
            });
          }else{
            userData.month[kpiId].pts = parseFloat(userData.month[kpiId].pts || 0) + pts;
            userData.month[kpiId].amt = parseFloat(userData.month[kpiId].amt || 0) + amt;
            userData.month.total = parseFloat(userData.month.total || 0) + pts;
            dispatch({
              type: types.UPDATE_MONTH_KPIS,
              usersKpis: updatedMonthRef
            });
          }
        }
        
        
        // // Update adminKpis if user is admin
        // if (isAdmin) {
        //     const updatedAdminKpis = { ...myKpis };
        //     if (!updatedAdminKpis.month[kpiId]) {
        //       updatedAdminKpis.month[kpiId] = { pts: result.data.points, amt: result.data.amount };
        //       updatedAdminKpis.month.total += result.data.points;
        //       dispatch({
        //         type: types.UPDATE_ADMIN_KPIS,
        //         adminKpis: updatedAdminKpis
        //       });
        //     }else{
        //       updatedAdminKpis.month[kpiId].pts += pts;
        //       updatedAdminKpis.month[kpiId].amt += amt;
        //       updatedAdminKpis.month.total += pts;
        //       dispatch({
        //         type: types.UPDATE_ADMIN_KPIS,
        //         adminKpis: updatedAdminKpis
        //       });
        //     }
        // }else{
                  // Update the appropriate time period
          if (isToday) {
            // Update today's scores
            if (!updatedMyKpis.today[kpiId]) {
              updatedMyKpis.today[kpiId] = { pts: result.data.points, amt: result.data.amount };
              updatedMyKpis.today.total += result.data.points;
            }else{
              updatedMyKpis.today[kpiId].pts += pts;
              updatedMyKpis.today[kpiId].amt += amt;
              updatedMyKpis.today.total += pts;
            }
            
            // Also update week and month
            if (!updatedMyKpis.week[kpiId]) {
              updatedMyKpis.week[kpiId] = { pts: result.data.points, amt: result.data.amount };
              updatedMyKpis.week.total += result.data.points;
            }else{
              updatedMyKpis.week[kpiId].pts += pts;
              updatedMyKpis.week[kpiId].amt += amt;
              updatedMyKpis.week.total += pts;
            }
            
            if (!updatedMyKpis.month[kpiId]) {
              updatedMyKpis.month[kpiId] = { pts: result.data.points, amt: result.data.amount };
              updatedMyKpis.month.total += result.data.points;
            }else{
              updatedMyKpis.month[kpiId].pts += pts;
              updatedMyKpis.month[kpiId].amt += amt;
              updatedMyKpis.month.total += pts;
            }
          } else {
            // For historical entries, update only the appropriate period
            const dateRef = moment(month);
            const week = moment().startOf("week");
            const monthStart = moment().startOf("month");
            
            if (dateRef >= week) {
              if (!updatedMyKpis.week[kpiId]) {
                updatedMyKpis.week[kpiId] = { pts: result.data.points, amt: result.data.amount };
                updatedMyKpis.week.total += result.data.points;
              }else{
                updatedMyKpis.week[kpiId].pts += pts;
                updatedMyKpis.week[kpiId].amt += amt;
                updatedMyKpis.week.total += pts;
              }
            }
            
            if (dateRef >= monthStart) {
              if (!updatedMyKpis.month[kpiId]) {
                updatedMyKpis.month[kpiId] = { pts: result.data.points, amt: result.data.amount };
                updatedMyKpis.month.total += result.data.points;
              }else{
                updatedMyKpis.month[kpiId].pts += pts;
                updatedMyKpis.month[kpiId].amt += amt;
                updatedMyKpis.month.total += pts;
              }
            }
          }
          
          // Update the Redux store with the new KPI data
          dispatch({
            type: types.UPDATE_MY_KPIS,
            myKpis: updatedMyKpis
          });
        // }
      }

    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    submitKpi,
    isSubmitting,
    error,
  };
};

export const useOfflineKpiSubmit = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submitOfflineKpi = async (kpiId, userId, month, data, companyId, id, entryId) => {
    setError(null);
    const monthFormatted = month ? moment(month).format("YYYY-MM") : null;
    setIsSubmitting(true);
    // Online flow
    try {
      const result = await ADD_USER_KPI({
        kpiId,
        companyId,
        userId,
        data,
        month: monthFormatted,
        entryId
      });

      if (result.data) {
        await deletePendingKpi(id);
        console.log('✅ Offline KPI deleted', id);
      }
      
      if (result.error) {
        throw new Error(result.error.message);
      }

    } catch (err) {
      if (err === "Request failed with status code 400") {
        await deletePendingKpi(id);
        console.log('✅ Offline KPI deleted - entry already existed2', id);
      }
      console.log('Error submitting offline kpi:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitOfflineKpi,
    isSubmitting,
    error,
  };
};

/**
 * Hook for updating total score
 */
export const useTotalScoreSave = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  const monthRef = useSelector(state => state.kpiReducer.usersKpis);
  const groupKpis = useSelector((state) => state.kpiReducer.groupKpis);

  const saveTotalScore = async (kpiId, userId, amount) => {
    setIsSaving(true);
    setError(null);

    console.log(kpiId, userId, amount);

    const kpiData = groupKpis?.find(kpi => kpi._id === kpiId);
    console.log("kpi Data:", kpiData);

    const isAnchorMetric = kpiData?.metricType === "anchor";
    let newpoints = 0;
    let convertedAmount = 0;

    if (isAnchorMetric) {
      const anchor = kpiData?.target * 100;
      const unit = kpiData?.optional?.unit * 100;
      const benchmark = kpiData?.optional?.benchmarkPoint;
      const belowBenchmark = kpiData?.optional?.belowBenchmark;
      const aboveBenchmark = kpiData?.optional?.aboveBenchmark;

      if (amount > anchor) {
        // Calculate points for exceeding anchor target
        const difference = amount - anchor;
        convertedAmount = (anchor + difference) / 100;
        const increments = difference / unit;
        newpoints = benchmark + (Math.floor(increments) * aboveBenchmark);
      } else if (amount < anchor) {
        // Calculate points for falling below anchor target  
        const difference = anchor - amount;
        convertedAmount = (anchor - difference) / 100;
        const increments = difference / unit;
        newpoints = benchmark - (Math.floor(increments) * belowBenchmark);
      }else{
        convertedAmount = kpiData?.target;
        newpoints = benchmark;
      }

      // Update the monthly scores
      const updatedMonthRef = JSON.parse(JSON.stringify(monthRef));
      const userData = updatedMonthRef.find(user => user.userId === userId);
      if (userData) {
        if (!userData.month[kpiId]) {
          userData.month[kpiId] = { pts: 0, amt: 0 };
        }
        const origValue = userData.month[kpiId].pts;
        userData.month[kpiId].pts = newpoints;
        userData.month[kpiId].amt = convertedAmount;
        userData.month.total = parseFloat(userData.month.total - origValue || 0) + newpoints;
      }
      dispatch({
        type: types.UPDATE_MONTH_KPIS,
        usersKpis: updatedMonthRef
      });
    }

    try {
      const result = await UPDATE_TOTAL_SCORE(kpiId, userId, amount);

      if (result.error) {
        throw new Error(result.error.message);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    saveTotalScore,
    isSaving,
    error,
  };
};

/**
 * Hook for updating KPI records from timeline
 */
export const useKpiUpdateScore = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  const monthRef = useSelector(state => state.kpiReducer.usersKpis);

  const updateKpiScore = async (kpiId, userId, oldAmount, oldPoints, newAmount, newPoints) => {
    setIsUpdating(true);
    setError(null);

    try {
        setError(null);
        // Update the monthly scores
        const updatedMonthRef = JSON.parse(JSON.stringify(monthRef));
        const userData = updatedMonthRef.find(user => user.userId === userId);
        if (userData) {
          if (!userData.month[kpiId]) {
            userData.month[kpiId] = { pts: 0, amt: 0 };
          }
          // Remove old values
          userData.month[kpiId].pts = parseFloat(userData.month[kpiId].pts || 0) - parseFloat(oldPoints || 0);
          userData.month[kpiId].amt = parseFloat(userData.month[kpiId].amt || 0) - parseFloat(oldAmount || 0);
          
          // Add new values 
          userData.month[kpiId].pts = parseFloat(userData.month[kpiId].pts || 0) + parseFloat(newPoints || 0);
          userData.month[kpiId].amt = parseFloat(userData.month[kpiId].amt || 0) + parseFloat(newAmount || 0);
          
          // Update total points
          userData.month.total = parseFloat(userData.month.total || 0) - parseFloat(oldPoints || 0);
          userData.month.total = parseFloat(userData.month.total || 0) + parseFloat(newPoints || 0);
        }
        dispatch({
          type: types.UPDATE_MONTH_KPIS,
          usersKpis: updatedMonthRef
        });
      // if (result.error) {
      //   throw new Error(result.error.message);
      // }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    updateKpiScore,
    isUpdating,
    error,
  };
};

/**
 * Hook for updating KPI records
 */
export const useKpiUpdate = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  const updateKpi = async (id, kpiId, amount) => {
    setIsUpdating(true);
    setError(null);

    try {
      const result = await UPDATE_USER_KPI(id, kpiId, amount);

      if (result.error) {
        throw new Error(result.error.message);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    updateKpi,
    isUpdating,
    error,
  };
};

/**
 * Hook for deleting KPI records
 */
export const useKpiDelete = () => {
  const { refetch } = useKpiData();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  const deleteKpi = async (kpiId) => {
    setIsDeleting(true);
    setError(null);

    try {
      const result = await DELETE_USER_KPI(kpiId);

      if (result.error) {
        throw new Error(result.error.message);
      }

      await refetch();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    deleteKpi,
    isDeleting,
    error,
  };
};

/**
 * Hook for updating workspace KPIs
 */
export const useWorkspaceKpiUpdate = () => {
  const { refetch } = useKpiData();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  const updateWorkspaceKpi = async (groupId, kpiObj) => {
    setIsUpdating(true);
    setError(null);

    try {
      const result = await UPDATE_GROUP_KPI(groupId, kpiObj);

      if (result.error) {
        throw new Error(result.error.message);
      }

      await refetch();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    updateWorkspaceKpi,
    isUpdating,
    error,
  };
};

/**
 * Hook for creating custom KPIs
 */
export const useCustomKpiCreate = () => {
  const { refetch } = useKpiData();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  const createCustomKpi = async (kpiData) => {
    setIsCreating(true);
    setError(null);

    try {
      const result = await CREATE_CUSTOM_KPI(kpiData);

      if (result.error) {
        throw new Error(result.error.message);
      }

      await refetch();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createCustomKpi,
    isCreating,
    error,
  };
};

/**
 * Hook for disabling workspace KPIs
 */
export const useWorkspaceKpiDisable = () => {
  const { refetch } = useKpiData();
  const [isDisabling, setIsDisabling] = useState(false);
  const [error, setError] = useState(null);

  const disableWorkspaceKpi = async (kpiId) => {
    setIsDisabling(true);
    setError(null);

    try {
      const result = await DISABLE_GROUP_KPI(kpiId);

      if (result.error) {
        throw new Error(result.error.message);
      }

      await refetch();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsDisabling(false);
    }
  };

  return {
    disableWorkspaceKpi,
    isDisabling,
    error,
  };
};

/**
 * Hook for getting previous KPIs
 */
export const useGetPreviousKpis = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Selectors for state
  const prevKpis = useSelector((state) => state.kpiReducer.historicKpis);
  const monthRef = useSelector((state) => state.kpiReducer.monthRef);
  const { companyId } = UseUserDetails();
  const workspace = UseSelectedWorkspace();

  const getPreviousKpis = useCallback(
    async (month, refresh = false) => {
      setIsLoading(true);
      setError(null);

      let dateRef;
      if (!month) {
        dateRef = monthRef;
      } else {
        dateRef = moment(month).format("YYYY-MM");
      }

      try {
        if (!refresh && prevKpis?.[dateRef]) {
          dispatch({
            type: types.SET_TO_HISTORIC_KPIS,
            monthRef: dateRef,
          });
        } else {
          const scoreKpis = await GET_ALL_USER_KPIS(
            companyId,
            workspace?._id,
            dateRef
          );

          const userScores = [];
          const sharedScores = [];

          scoreKpis.data.scores.forEach((score) => {
            if (score.scope === "User") {
              userScores.push(score);
            } else {
              sharedScores.push(...score.days);
            }
          });

          if (sharedScores.length) {
            scoreKpis.data.users.forEach((user) => {
              const idx = userScores.findIndex((u) => u.ownerId === user);
              if (idx >= 0) {
                userScores[idx].days.push(...sharedScores);
              } else {
                userScores.push({
                  ownerId: user,
                  days: [...sharedScores],
                });
              }
            });
          }

          const usersKpis = userScores.map((userData) => {
            const summary = {
              userId: userData.ownerId,
              month: { total: 0 },
            };

            for (let d = 0; d < userData.days.length; d++) {
              const dayData = userData.days[d];
              addKpiObjects(summary.month, dayData.kpis);
            }

            return summary;
          });

          const newKpiHistory = {};
          newKpiHistory[dateRef] = usersKpis.sort(
            (a, b) => b.month.total - a.month.total
          );

          dispatch({
            type: types.GET_PREVIOUS_KPIS_SUCCESS,
            historicKpis: newKpiHistory,
            monthRef: dateRef,
          });
        }
      } catch (err) {
        setError(err.message);
        dispatch({ type: types.GET_PREVIOUS_KPIS_FAILED, error: err.message });
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, prevKpis, companyId, workspace, monthRef]
  );

  return {
    getPreviousKpis,
    isLoading,
    error,
    monthRef,
  };
};